/* Copyright (c) 2017 Backpack Health LLC */

/* global IS_ANDROID_BUILD, cordova */

import React, { FC } from 'react';
import { FormattedMessage } from 'react-intl';
import * as _ from 'lodash';

import { ListItem } from '../../../../components/List';
import { ExternalLink, MailToLink } from '../../../../components/TextLink';
import IconEmail from '../../../../components/icons/IconEmail';
import IconPhone from '../../../../components/icons/IconPhone';
import IconFacebook from '../../../../components/icons/IconFacebook';
import IconInstagram from '../../../../components/icons/IconInstagram';
import IconTwitter from '../../../../components/icons/IconTwitter';
import IconYoutube from '../../../../components/icons/IconYoutube';
import IconLink from '../../../../components/icons/IconLink';
import InsetRow from '../../../../components/InsetRow';
import PhoneFormatter from '../../../../../utilities/PhoneFormatter';
interface ContactEmailProps {
  email: string;
  className: string;
}
const ContactEmail: FC<ContactEmailProps> = ({ email, className }) => (
  <InsetRow
    icon={<IconEmail />}
    style={{ paddingBottom: 24, wordBreak: 'break-word' }}
    flexStyle={{ flexWrap: 'nowrap' }}
  >
    <MailToLink cutProtocols email={email} className={className}>
      {email}
    </MailToLink>
  </InsetRow>
);

interface ContactWebsiteProps {
  website: string;
  className: string;
}
const ContactWebsite: FC<ContactWebsiteProps> = ({ website, className }) => (
  <InsetRow
    icon={<IconLink />}
    style={{ paddingBottom: 24, wordBreak: 'break-word' }}
    flexStyle={{ flexWrap: 'nowrap' }}
  >
    <ExternalLink cutProtocols href={website} className={className}>
      {website}
    </ExternalLink>
  </InsetRow>
);

interface ContactPhoneProps {
  phone: string;
  iso2_code: string;
  className: string;
};
const ContactPhone: FC<ContactPhoneProps> = ({ phone, iso2_code, className }) => (
  <InsetRow
    icon={<IconPhone />}
    style={{ paddingBottom: 24, wordBreak: 'break-word' }}
    flexStyle={{ flexWrap: 'nowrap' }}
  >
    <ExternalLink href={`${PhoneFormatter.formatTelephoneLink(iso2_code, phone)}`} className={className}>
      +{PhoneFormatter.getCountryCallingCode(iso2_code)} {phone}
    </ExternalLink>
  </InsetRow>
);

interface SocialLinkProps {
  name: string;
  value: string;
  Icon: React.FC<any>;
  className: string;
};
const SocialLink: FC<SocialLinkProps> = ({ Icon, name, value, className }) => (
  <InsetRow
    icon={<Icon />}
    style={{ paddingBottom: 24, wordBreak: 'break-word' }}
    flexStyle={{ flexWrap: 'nowrap' }}
  >
    <FormattedMessage id={`contactInfo.${name}.url`} values={{ string1: value }}>
      {
        (url) => (
          <ExternalLink href={url} className={className}>
            <FormattedMessage id={`contactInfo.${name}.field.prefix`} values={{ string1: value }} />
          </ExternalLink>
        )
      }
    </FormattedMessage>
  </InsetRow>
);

interface ContactListProps {
  contact: any;
}
export const ContactList: FC<ContactListProps> = ({ contact }) => {
  const className = 'theme-group accent color font-body';
  const items = _.compact([
    contact.url ? <ContactWebsite key={1} website={contact.url} className={className} /> : null,
    contact.email ? <ContactEmail key={2} email={contact.email} className={className} /> : null,
    contact.phone ? <ContactPhone key={3} phone={contact.phone} iso2_code={contact.iso2_code} className={className} /> : null,
  ]);

  return items.length === 0 ? null : (
    <ListItem>
      {items}
    </ListItem>
  );
};

interface SocialListProps {
  contact: {
    facebook?: string;
    twitter?: string;
    instagram?: string;
    youtube?: string;
  };
  style: any;
};
export const SocialList: FC<SocialListProps> = ({ contact, style }) => {
  const className = IS_ANDROID_BUILD ? 'theme-group accent color font-footnote' : 'theme-group accent color font-body';
  const items = _.compact([
    contact.facebook ? <SocialLink key={1} name="facebook" value={contact.facebook} Icon={IconFacebook} className={className} /> : null,
    contact.twitter ? <SocialLink key={2} name="twitter" value={contact.twitter} Icon={IconTwitter} className={className} /> : null,
    contact.instagram ? <SocialLink key={3} name="instagram" value={contact.instagram} Icon={IconInstagram} className={className} /> : null,
    contact.youtube ? <SocialLink key={4} name="youtube" value={contact.youtube} Icon={IconYoutube} className={className} /> : null,
  ]);

  return items.length === 0 ? null : (
    <ListItem style={style}>
      {items}
    </ListItem>
  );
};
